
import { useState, useEffect } from 'react';
import { Exercise } from '@/models/LearnTypes';

interface UseExerciseTransitionProps {
  currentExercise: Exercise;
  isLastExercise: boolean;
  showAnswer: boolean;
  isInputCorrect: boolean | null;
  onNext: () => void;
}

/**
 * Hook that manages transitions between exercises
 * Handles animations and timing for moving from one exercise to the next
 * Controls the visual flow of cards entering, exiting, and returning to the stack
 */
export const useExerciseTransition = ({
  currentExercise,
  isLastExercise,
  showAnswer,
  isInputCorrect,
  onNext
}: UseExerciseTransitionProps) => {
  // Animation and transition states
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentCard, setCurrentCard] = useState(currentExercise);
  const [nextCard, setNextCard] = useState<Exercise | null>(null);
  const [nextCardReady, setNextCardReady] = useState(false);
  const [completionTransition, setCompletionTransition] = useState(false);
  const [initialAnimation, setInitialAnimation] = useState(true);
  const [isReturningToStack, setIsReturningToStack] = useState(false);
  
  // Track initial load for entry animation
  const [initialLoad, setInitialLoad] = useState(true);
  
  useEffect(() => {
    // Set initial animation on first render only
    const timer = setTimeout(() => {
      setInitialLoad(false);
    }, 1500); // Allow more time for the entry animation
    
    return () => clearTimeout(timer);
  }, []);
  
  // Ensure currentCard always reflects the currentExercise
  useEffect(() => {
    setCurrentCard(currentExercise);
    
    // Reset animation states when currentExercise changes
    setIsAnimating(false);
    setNextCardReady(false);
    setIsReturningToStack(false);
  }, [currentExercise]);

  // Automatically transition for spelling exercises when answered correctly
  useEffect(() => {
    let timer: number;
    
    if (currentExercise?.type === 'spelling' && 
        showAnswer && 
        isInputCorrect === true) {
      timer = window.setTimeout(() => {
        handleCardTransition(false);
      }, 900); // Increased delay for spelling exercises to allow animations
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showAnswer, isInputCorrect, currentExercise?.type]);

  /**
   * Handle transition from one card to the next with proper animations
   * Manages the visual flow of cards exiting, entering, and returning to stack
   * 
   * @param wasManuallyFlipped - Whether the card was manually flipped (needs to return to stack)
   */
  const handleCardTransition = (wasManuallyFlipped: boolean = false) => {
    // For the last exercise, show completion animation and call onNext
    if (isLastExercise) {
      setCompletionTransition(true);
      onNext(); // This will trigger level completion logic
      return;
    }
    
    // If card was manually flipped, show returning to stack animation
    if (wasManuallyFlipped) {
      setIsReturningToStack(true);
      
      // Allow time for the return animation to play
      setTimeout(() => {
        setIsReturningToStack(false);
        setIsAnimating(true);
        
        // Proceed with standard transition after the return animation
        setTimeout(() => {
          // Update UI state before calling onNext to prevent flashing
          onNext();
          
          // Reset animation state after a delay
          setTimeout(() => {
            setIsAnimating(false);
          }, 150); // Shorter delay after transition completes
        }, 650); // Match the return-to-stack animation duration
      }, 700); // Time to show the return animation
    } else {
      // Standard transition animation
      setIsAnimating(true);
      
      // For non-last exercises, call onNext after animation delay
      setTimeout(() => {
        onNext();
        
        // Reset animation state after a small delay
        setTimeout(() => {
          setIsAnimating(false);
        }, 150); // Shorter delay after transition completes
      }, 550); // Match the slide-out animation duration
    }
  };

  return {
    isAnimating,
    currentCard,
    nextCard,
    nextCardReady,
    completionTransition,
    initialAnimation,
    initialLoad,
    isReturningToStack,
    handleCardTransition
  };
};
